import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api/axios';
import '../styles/ProfileView.css';

const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'fake_profile', label: 'Fake profile' },
  { value: 'inappropriate_photos', label: 'Inappropriate photos' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'other', label: 'Other' },
];

export default function ProfileViewPage() {
  const { id: userId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photoIndex, setPhotoIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await API.get(`/api/users/${userId}`);
        if (!cancelled) setProfile(response.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? 'Profile not found'
              : 'Failed to load profile'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const isLimited = profile && !profile.bio && !profile.photos;

  const photos = profile?.photos || [];
  const currentPhoto = photos[photoIndex];

  const handlePhotoNav = (direction) => {
    if (!photos.length) return;
    if (direction === 'next' && photoIndex < photos.length - 1) {
      setPhotoIndex(photoIndex + 1);
    } else if (direction === 'prev' && photoIndex > 0) {
      setPhotoIndex(photoIndex - 1);
    }
  };

  const handleReportSubmit = async (reason, details) => {
    try {
      await API.post('/api/reports', {
        reported_id: userId,
        reason,
        details,
      });
      setReportOpen(false);
      alert('Report submitted. Thanks for keeping the community safe.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit report');
    }
  };

  const handleBlock = async () => {
    try {
      await API.post(`/api/blocks/${userId}`);
      setBlockConfirm(false);
      alert('User blocked');
      navigate('/matches');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to block user');
    }
  };

  if (loading) {
    return (
      <div className="profile-view-page">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-view-page">
        <div className="profile-error">
          <p>{error}</p>
          <button onClick={() => navigate(-1)}>Go back</button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  // Limited view (not matched): show only basic info
  if (isLimited) {
    return (
      <div className="profile-view-page">
        <ProfileHeader onBack={() => navigate(-1)} />

        <div className="profile-limited">
          {profile.primary_photo ? (
            <img src={profile.primary_photo} alt="" className="limited-photo" />
          ) : (
            <div className="limited-photo-placeholder">
              {profile.full_name?.[0] || '?'}
            </div>
          )}
          <h1>{profile.full_name}, {profile.age}</h1>
          {profile.denomination && (
            <span className="profile-badge">{profile.denomination}</span>
          )}
          <p className="limited-message">
            Match with this user to see their full profile.
          </p>
        </div>
      </div>
    );
  }

  // Full view (matched or own profile)
  return (
    <div className="profile-view-page">
      <ProfileHeader
        onBack={() => navigate(-1)}
        onMenu={() => setMenuOpen(!menuOpen)}
        showMenu={true}
      />

      {menuOpen && (
        <div className="action-menu" onClick={() => setMenuOpen(false)}>
          <div className="action-menu-content" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                setMenuOpen(false);
                setReportOpen(true);
              }}
            >
              Report user
            </button>
            <button
              className="action-danger"
              onClick={() => {
                setMenuOpen(false);
                setBlockConfirm(true);
              }}
            >
              Block user
            </button>
            <button onClick={() => setMenuOpen(false)} className="action-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Photo carousel */}
      <div className="profile-photo-area">
        {currentPhoto ? (
          <img src={currentPhoto.url} alt="" className="profile-photo" />
        ) : (
          <div className="profile-photo-placeholder">
            {profile.full_name?.[0] || '?'}
          </div>
        )}

        {photos.length > 1 && (
          <>
            <div className="photo-dots">
              {photos.map((_, i) => (
                <span
                  key={i}
                  className={`photo-dot ${i === photoIndex ? 'active' : ''}`}
                />
              ))}
            </div>

            <button
              className="photo-arrow left"
              onClick={() => handlePhotoNav('prev')}
              disabled={photoIndex === 0}
              aria-label="Previous photo"
            >
              ‹
            </button>
            <button
              className="photo-arrow right"
              onClick={() => handlePhotoNav('next')}
              disabled={photoIndex === photos.length - 1}
              aria-label="Next photo"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Info section */}
      <div className="profile-info">
        <div className="profile-name-row">
          <h1>{profile.full_name}</h1>
          {profile.age != null && <span className="profile-age">{profile.age}</span>}
        </div>

        <div className="profile-tags">
          {profile.denomination && (
            <span className="profile-badge">{profile.denomination}</span>
          )}
          {profile.faith_level > 0 && (
            <span className="profile-badge faith">
              Faith level {profile.faith_level}
            </span>
          )}
        </div>

        {profile.home_church && (
          <div className="profile-section">
            <h3>Home church</h3>
            <p>{profile.home_church}</p>
          </div>
        )}

        {profile.bio && (
          <div className="profile-section">
            <h3>About</h3>
            <p>{profile.bio}</p>
          </div>
        )}

        {profile.interests && profile.interests.length > 0 && (
          <div className="profile-section">
            <h3>Interests</h3>
            <div className="interest-chips">
              {profile.interests.map((interest) => (
                <span key={interest} className="interest-chip">
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report modal */}
      {reportOpen && (
        <ReportModal
          onCancel={() => setReportOpen(false)}
          onSubmit={handleReportSubmit}
        />
      )}

      {/* Block confirm */}
      {blockConfirm && (
        <ConfirmDialog
          title="Block this user?"
          body="They won't be able to message or match with you. Your match will be removed."
          confirmLabel="Block"
          dangerous={true}
          onConfirm={handleBlock}
          onCancel={() => setBlockConfirm(false)}
        />
      )}
    </div>
  );
}

// =====================================================================
// Header subcomponent
// =====================================================================
function ProfileHeader({ onBack, onMenu, showMenu }) {
  return (
    <header className="profile-view-header">
      <button className="profile-back" onClick={onBack} aria-label="Back">
        ←
      </button>
      {showMenu && (
        <button className="profile-menu" onClick={onMenu} aria-label="More">
          ⋯
        </button>
      )}
    </header>
  );
}

// =====================================================================
// Report modal
// =====================================================================
function ReportModal({ onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    await onSubmit(reason, details);
    setSubmitting(false);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2>Report user</h2>
        <p className="modal-sub">Help us keep Godly Match safe.</p>

        <div className="report-reasons">
          {REPORT_REASONS.map((r) => (
            <label
              key={r.value}
              className={`report-option ${reason === r.value ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
              />
              {r.label}
            </label>
          ))}
        </div>

        <textarea
          placeholder="Additional details (optional)"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1000}
          rows={3}
          className="report-details"
        />

        <div className="modal-actions">
          <button onClick={onCancel} className="modal-cancel">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className="modal-confirm"
          >
            {submitting ? 'Submitting...' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Confirm dialog (reusable)
// =====================================================================
function ConfirmDialog({ title, body, confirmLabel, dangerous, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card small" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <p>{body}</p>
        <div className="modal-actions">
          <button onClick={onCancel} className="modal-cancel">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={dangerous ? 'modal-danger' : 'modal-confirm'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}